import React, { useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { MdOutlineCall, MdApproval, MdLocationOn } from 'react-icons/md';
import { BiPlus } from 'react-icons/bi';
import SidebarItem from './SidebarItem';
import { IoChevronDownSharp } from 'react-icons/io5';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAgent } from 'providers/context/AgentProvider';
import Spinner from 'components/Common/Spinner';
import { useGetAgents } from 'apis/business/agent/hook';
import * as urls from 'routes/config';
import { WEB_EMBED_SCRIPT } from 'env';
import PerformanceIcon from 'assets/icons/PerformanceIcon';
import CallLogsIcon from 'assets/icons/CallLogsIcon';
import ConfigureIcon from 'assets/icons/ConfigureIcon';
import { CacheKeys } from 'utils/cacheKeys';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { queryClient } from 'providers/AllProviders';
import { deleteAgent } from 'apis/business/agent/api';
import WorksheetIcon from 'assets/icons/Worksheet';
import TalkButton from '../../Common/TalkButton';

interface Props {
  isMenuOpen?: boolean;
  handleClose?: () => void;
}

export default function Sidebar({ isMenuOpen, handleClose }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { workspaceId, agentId } = useParams();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const { setSelectedAgent, setSelectedAgentId } = useAgent();
  const { data: agents, isLoading } = useGetAgents();
  const open = Boolean(anchorEl);

  useEffect(() => {
    const filteredAgent = agents?.data?.find((item) => item.id === Number(agentId));
    const agentIndex = agents?.data?.findIndex((item) => item.id === Number(agentId));

    setSelectedIndex(agentIndex !== -1 ? Number(agentIndex) : 0);
    setSelectedAgent(filteredAgent ?? agents?.data[0]);
  }, [agents, setSelectedAgent, setSelectedIndex, agentId]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (_: React.MouseEvent<HTMLElement>, index: number) => {
    setSelectedAgent(agents?.data?.[index]);
    setSelectedAgentId(agents?.data?.[index]?.id);
    localStorage.setItem(CacheKeys.selectedAgentId, String(agents?.data?.[index]?.id));
    setSelectedIndex(index);
    navigate(`${urls.agentBase(workspaceId, agents?.data?.[index]?.id)}/${urls.home.configure}`);
    queryClient.resetQueries();
    setAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const existingScript = document.querySelector(`script[src="${WEB_EMBED_SCRIPT}"]`);
    if (!existingScript) {
      const script1 = document.createElement('script');
      script1.src = WEB_EMBED_SCRIPT ?? '';
      script1.id = 'embed-script';
      script1.async = true;

      document.head.appendChild(script1);

      return () => {
        document.head.removeChild(script1);
      };
    }
  }, []);

  const handleDeleteAgent = async (id: number) => {
    const response = await deleteAgent(workspaceId!, id);
    if ((response as any)?.meta?.message) {
      queryClient.invalidateQueries({
        queryKey: ['agents', workspaceId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', workspaceId, id],
        refetchType: 'active'
      });
      const leftAgents = agents?.data.filter((item) => item.id !== id);
      if (leftAgents?.length) {
        setSelectedAgent(leftAgents?.[0]);
        setSelectedAgentId(leftAgents?.[0]?.id);
        localStorage.setItem(CacheKeys.selectedAgentId, String(leftAgents?.[0]?.id));
        setSelectedIndex(0);
        navigate(`${urls.agentBase(workspaceId, leftAgents?.[0]?.id)}/${urls.home.configure}`);
      } else {
        setSelectedAgent(undefined);
        setSelectedAgentId(undefined);
        localStorage.removeItem(CacheKeys.selectedAgentId);
        localStorage.removeItem(CacheKeys.agentIds);
        navigate(`${urls.base(workspaceId)}/${urls.onBoarding.create}`);
      }
    }
  };

  const drawerContent = (
    <List sx={{ paddingTop: 2 }}>
      <Box marginX={2}>
        <Box>
          <List component='nav' aria-label='Device settings'>
            <ListItemButton
              id='lock-button'
              aria-haspopup='listbox'
              aria-controls='lock-menu'
              aria-label='when device is locked'
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClickListItem}
              sx={{
                border: '1px solid #ccc',
                borderRadius: 2,
                display: 'block'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemText>
                  <Typography variant='h6' component='p' noWrap title={agents?.data?.[selectedIndex]?.name}>
                    {agents?.data?.[selectedIndex]?.name}
                  </Typography>
                </ListItemText>
                <IoChevronDownSharp />
              </Box>
              <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px'>
                <Box height='16px' width='16px'>
                  <MdApproval />
                </Box>
                <Typography variant='body2' color={theme.palette.secondary.main} textTransform='capitalize'>
                  {agents?.data?.[selectedIndex]?.agent_interaction}
                </Typography>
              </Box>
              <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px' marginY={0.2}>
                <Box height='16px' width='16px'>
                  <MdLocationOn />
                </Box>
                <Tooltip placement='top' title={agents?.data?.[selectedIndex]?.business_address}>
                  <Typography
                    variant='body2'
                    color={theme.palette.secondary.main}
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {agents?.data?.[selectedIndex]?.business_address ?? '---'}
                  </Typography>
                </Tooltip>
              </Box>
              <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px'>
                <Box height='16px' width='16px'>
                  <MdOutlineCall />
                </Box>
                <Typography variant='body2' color={theme.palette.secondary.main}>
                  {agents?.data?.[selectedIndex]?.phone_number ?? '---'}
                </Typography>
              </Box>
            </ListItemButton>
          </List>
          <Menu
            id='lock-menu'
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'lock-button',
              role: 'listbox'
            }}
            slotProps={{ paper: { sx: { width: 205 } } }}
          >
            <Box
              display='flex'
              justifyContent='center'
              gap={0.5}
              alignItems='center'
              border='1px solid #F3F3F3'
              color={theme.palette.secondary.main}
              margin={1}
              paddingY={0.75}
              borderRadius={0.5}
              sx={{
                textDecoration: 'none'
              }}
              component={Link}
              to={`${urls.base(workspaceId)}/${urls.onBoarding.selectAgent}`}
              onClick={() => handleMenuClose()}
            >
              <BiPlus />
              <Typography>Add Agent</Typography>
            </Box>
            {agents?.data.map((option, index) => (
              <MenuItem
                key={option.id}
                selected={index === selectedIndex}
                onClick={(event) => handleMenuItemClick(event, index)}
              >
                <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                    title={option.name}
                  >
                    {option.name}
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAgent(option?.id);
                    }}
                  >
                    <RiDeleteBin6Line style={{ color: 'red', fontSize: 16 }} />
                  </IconButton>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      <ListItem>
        <TalkButton />
      </ListItem>

      <SidebarItem to={urls.home.performance} icon={<PerformanceIcon />} label='Performance' />
      <SidebarItem to={urls.home.callLogs} icon={<CallLogsIcon />} label='Call Logs' />
      <SidebarItem to={urls.worksheetBase(workspaceId, agentId)} icon={<WorksheetIcon />} label='Worksheet' />
      <SidebarItem
        to={
          agentId
            ? `${urls.agentBase(workspaceId, agentId)}/${urls.home.configure}`
            : `${urls.base(workspaceId)}/${urls.home.configure}`
        }
        icon={<ConfigureIcon />}
        label='Configure'
      />
    </List>
  );

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <Drawer
      variant={isMediumScreen ? 'temporary' : 'permanent'}
      open={isMediumScreen ? isMenuOpen : true}
      onClose={handleClose}
      anchor='left'
      sx={{
        width: 240,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 240,
          boxSizing: 'border-box',
          top: isMediumScreen ? 0 : '65px',
          backgroundColor: theme.palette.background.paper
        }
      }}
      ModalProps={{
        keepMounted: true
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
